/**
 * Full-page entry gate: age + research-use compliance confirmation shown
 * before a visitor can access the storefront.
 *
 * Behaviour:
 * - Shown once per browser session (sessionStorage), matching the pattern
 *   used elsewhere in this theme (see email-signup-popup.js's original
 *   design) — reappears fresh next session rather than being suppressed
 *   forever.
 * - All four checkboxes must be checked before "Continue" becomes enabled.
 *   This is a real gate, not a dismissable promo: there is no close button,
 *   no click-outside-to-dismiss, and no Escape handling.
 * - "I cannot confirm — leave website" sends the visitor away from the site
 *   entirely rather than just closing the gate.
 */

const STORAGE_KEY = 'entry-gate-confirmed';
const LEAVE_URL = 'https://www.google.com';

class EntryGate extends HTMLElement {
  connectedCallback() {
    if (this.#alreadyConfirmed()) {
      this.remove();
      return;
    }

    this.checkboxes = Array.from(this.querySelectorAll('.entry-gate__checkbox'));
    this.continueButton = this.querySelector('.entry-gate__continue');
    this.leaveLink = this.querySelector('.entry-gate__leave');

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.#updateContinueState());
    });

    this.continueButton?.addEventListener('click', () => {
      if (this.continueButton.disabled) return;
      this.#remember();
      this.remove();
    });

    this.leaveLink?.addEventListener('click', (event) => {
      event.preventDefault();
      window.location.href = LEAVE_URL;
    });

    document.body.style.overflow = 'hidden';
    this.addEventListener('disconnectedCallback-cleanup', () => {}, { once: true });

    // Restore scrolling once the gate is actually removed from the page,
    // whichever way that happens.
    const observer = new MutationObserver(() => {
      if (!this.isConnected) {
        document.body.style.overflow = '';
        observer.disconnect();
      }
    });
    observer.observe(this.parentNode, { childList: true });
  }

  #updateContinueState() {
    const allChecked = this.checkboxes.every((checkbox) => checkbox.checked);
    if (this.continueButton) {
      this.continueButton.disabled = !allChecked;
    }
  }

  #alreadyConfirmed() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      // sessionStorage can throw in private-browsing modes on some browsers.
      // Fail open: show the gate rather than silently skipping a legal
      // confirmation step.
      return false;
    }
  }

  #remember() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      // Ignore storage errors (e.g. private browsing / storage disabled).
      // The gate will simply show again on the next page load this visit.
    }
  }
}

if (!customElements.get('entry-gate')) {
  customElements.define('entry-gate', EntryGate);
}
